import { zodResolver } from '@hookform/resolvers/zod';
import { Box, InputAdornment, TextField } from '@mui/material';
import { DEFAULT_CURRENCY, REVISIONED_LINE_ITEM_TYPES } from '@weft/types';
import { isNotNullOrUndefined } from '@weft/utils';
import { getCurrencySymbol } from 'apps/app/src/services/currency-symbol-mapper';
import type { PropsWithChildren } from 'react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { QueryKey } from 'react-query';
import { vyFormRegister } from '../../../../../utils/react-hook-forms-utils';
import { useDynamicInvoice } from '../../dynamic-invoice-context';
import type { RevisionedLineItemDialogSubmitAction } from '../RevisionedLineItemDialogForm';
import { RevisionedLineItemDialogForm, RevisionedLineItemDialogSubmitActions } from '../RevisionedLineItemDialogForm';
import type { FixedRevisionedLineItemForm } from './revisioned-line-item-dialog-forms-types/fixed-revisioned-line-item-form';
import { FixedRevisionedLineItemFormSchema } from './revisioned-line-item-dialog-forms-types/fixed-revisioned-line-item-form';

type AddFixedRevisionedLineItemDialogProps = PropsWithChildren<{
  revisionedLineItemId?: string;
  open: boolean;
  onClose?: () => void;
  invoiceId: string;
  defaultValues?: Partial<FixedRevisionedLineItemForm>;
  submitAction?: RevisionedLineItemDialogSubmitAction;
  queryKeyToInvalidate?: QueryKey;
}>;

export const FixedRevisionedLineItemDialogForm: React.FC<AddFixedRevisionedLineItemDialogProps> = ({
  revisionedLineItemId,
  open,
  onClose,
  invoiceId,
  defaultValues,
  submitAction,
  queryKeyToInvalidate,
}) => {
  const form = useForm<FixedRevisionedLineItemForm>({
    resolver: zodResolver(FixedRevisionedLineItemFormSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

  const fixedRevisionedLineItemNameRegister = vyFormRegister(form.register, 'name');
  const fixedRevisionedLineItemDescriptionRegister = vyFormRegister(form.register, 'description');
  const fixedRevisionedLineItemPriceRegister = form.register('price', {
    valueAsNumber: true,
  });

  const title = submitAction === RevisionedLineItemDialogSubmitActions.Create ? 'Add product or service fee to this invoice' : 'Update product or service fee in this invoice';
  const submitButtonLabel = submitAction === RevisionedLineItemDialogSubmitActions.Create ? 'Add item' : 'Update';

  const { dynamicInvoiceData } = useDynamicInvoice();

  const currency = dynamicInvoiceData.customer.settings?.currency ?? DEFAULT_CURRENCY;

  return (
    <RevisionedLineItemDialogForm
      invoiceId={invoiceId}
      revisionedLineItemId={revisionedLineItemId}
      open={open}
      onClose={onClose}
      revisionedLineItemType={REVISIONED_LINE_ITEM_TYPES.Fixed}
      form={form}
      submitAction={submitAction}
      title={title}
      submitButtonLabel={submitButtonLabel}
      queryKeyToInvalidate={queryKeyToInvalidate}
    >
      <TextField
        {...fixedRevisionedLineItemNameRegister}
        label="Item Name"
        placeholder="Type Name Here..."
        size='medium'
        fullWidth
        sx={{ padding: '10px' }}
        error={isNotNullOrUndefined(form.formState.errors[fixedRevisionedLineItemNameRegister.name])}
        helperText={form.formState.errors[fixedRevisionedLineItemNameRegister.name]?.message}
      />
      <Box sx={{
        display: 'flex',
        gap: '32px',
        padding: '10px',
      }}
      >
        <TextField
          {...fixedRevisionedLineItemDescriptionRegister}
          label="Description"
          placeholder="Type Description Here..."
          size='medium'
          fullWidth
          error={isNotNullOrUndefined(form.formState.errors[fixedRevisionedLineItemDescriptionRegister.name])}
          helperText={form.formState.errors[fixedRevisionedLineItemDescriptionRegister.name]?.message}
        />
        <TextField
          {...fixedRevisionedLineItemPriceRegister}
          label="Price"
          placeholder="0.00"
          size='medium'
          type="number"
          InputProps={{
            startAdornment: <InputAdornment position="start">{getCurrencySymbol(currency)}</InputAdornment>,
          }}
          error={isNotNullOrUndefined(form.formState.errors[fixedRevisionedLineItemPriceRegister.name])}
          helperText={form.formState.errors[fixedRevisionedLineItemPriceRegister.name]?.message}
        />
      </Box>
    </RevisionedLineItemDialogForm>
  );
};